import { useEffect, useRef } from 'react';

import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { usePartnerLookup } from 'components/partner_lookup/context';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Card, Col, Collapse, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { PartnerTypes } from 'apis/PartnerApi';
import { CountryApi } from 'apis/CountryApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

type PartnerTypeOption = {
    Id: number;
    Name: string;
};

type CountryOption = {
    Id: number;
    Name: string;
};

type StateOption = {
    Id: number;
    Name: string;
};

const SECTOR_OPTIONS = [
    { Id: 0, Name: 'All' },
    { Id: 1, Name: 'K12 Education' },
    { Id: 4, Name: 'Higher Education' },
    { Id: 2, Name: 'Government' },
    { Id: 3, Name: 'Commercial' }
];

const STATUS_OPTIONS = [
    { Id: -1, Name: 'All' },
    { Id: 1, Name: 'Active' },
    { Id: 0, Name: 'Inactive' }
];

export const PartnerLookupFilters = props => {

    const [state, setState] = useSuperState({
        advancedOpen: false,
        countries: [] as CountryOption[],
        partnerTypeOptions: [] as PartnerTypeOption[],
        states: [] as StateOption[]
    });
    const { advancedOpen, countries, partnerTypeOptions, states } = state;

    const { highestRole } = useApp();
    const { formatMessage } = useIntl();
    const { changeFilters, clearFilters, disabled, filters, partnerType, search, singleSearch } = usePartnerLookup();

    const mountedRef = useRef(false);

    const fetchInitialData = async () => {

        try {

            const countriesData = await CountryApi.GetAll();
            const allCountries = [{ Id: 0, Name: 'All' }, ...countriesData];
            setState({ countries: allCountries });

        } catch {

            // Countries are optional for filtering
        }

        buildPartnerTypeOptions();

    };

    const buildPartnerTypeOptions = () => {

        const options: PartnerTypeOption[] = [{ Id: 0, Name: 'Pick One...' }];
        const roleId = highestRole?.id ?? 0;

        if (roleId === 1) {

            options.push({ Id: PartnerTypes.Distributor, Name: 'Distributors' });
            options.push({ Id: PartnerTypes.Reseller, Name: 'Resellers' });
            options.push({ Id: PartnerTypes.TestingCenter, Name: 'Testing Centers' });

        } else if (roleId === 2) {

            options.push({ Id: PartnerTypes.Reseller, Name: 'Resellers' });
            options.push({ Id: PartnerTypes.TestingCenter, Name: 'Testing Centers' });

        } else if (roleId === 3 || roleId === 4) {

            options.push({ Id: PartnerTypes.TestingCenter, Name: 'Testing Centers' });

        }

        setState({ partnerTypeOptions: options });

    };

    const fetchStates = async (countryId: number) => {

        try {

            const statesData = await CountryApi.GetStates(countryId);
            const allStates = [{ Id: 0, Name: 'All' }, ...statesData];
            setState({ states: allStates });

        } catch {

            setState({ states: [] });

        }

    };

    const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && search();

    useEffect(() => {

        fetchInitialData();

    }, []);

    useEffect(() => {

        if (!mountedRef.current) {

            mountedRef.current = true;
            return;

        }

        if (filters.countryId > 0) {

            fetchStates(filters.countryId);

        } else {

            setState({ states: [] });
            changeFilters({ stateId: 0 });

        }

    }, [filters.countryId]);

    const selectedPartnerType = partnerTypeOptions.find(o => o.Id === filters.partnerType) ?? partnerTypeOptions[0];
    const selectedSector = SECTOR_OPTIONS.find(o => o.Id === filters.sector) ?? SECTOR_OPTIONS[0];
    const selectedCountry = countries.find(o => o.Id === filters.countryId) ?? countries[0];
    const selectedState = states.find(o => o.Id === filters.stateId) ?? states[0];
    const selectedStatus = STATUS_OPTIONS.find(o => o.Id === filters.status) ?? STATUS_OPTIONS[0];

    return (
        <Card className={'partner-lookup-filters'} onKeyDown={handleKeyDown}>
            <Card.Body className="mb-3">
                <div className="d-flex align-items-end gap-2 mb-3">
                    <GMetrixInput
                        label={formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' })}
                        onChange={e => changeFilters({ nameSearch: e.target.value })}
                        placeholder={formatMessage({ id: 'search_by_name_email_or_customer_id.e7a3f7f7', defaultMessage: 'Search by name, email, or customer ID' })}
                        readOnly={disabled}
                        value={filters.nameSearch}
                        width="flex-grow-1"
                    />
                    <div className="d-flex gap-2 flex-shrink-0">
                        <GMetrixButton
                            disabled={disabled}
                            onClick={search}
                            type="button"
                            variant="primary"
                        >
                            {<FormattedMessage defaultMessage="Search" id="search.0ab06b2c" />}
                        </GMetrixButton>
                        {!singleSearch && (
                            <GMetrixButton
                                disabled={disabled}
                                onClick={clearFilters}
                                type="button"
                                variant="outline-primary"
                            >
                                {<FormattedMessage defaultMessage="Clear Filters" id="clear_filters.43f50aba" />}
                            </GMetrixButton>
                        )}
                    </div>
                </div>

                {!partnerType && partnerTypeOptions.length > 1 && (
                <Row>
                    <Col className="mb-2" lg={3} md={4}>
                        <GMetrixDropdown
                            dataItemKey="Id"
                            dataItems={partnerTypeOptions}
                            disabled={disabled}
                            label={formatMessage({ id: 'show_me.e9418a3d', defaultMessage: 'Show Me', description: 'New' })}
                            onChange={(val: PartnerTypeOption) => changeFilters({ partnerType: val.Id })}
                            textField="Name"
                            value={selectedPartnerType}
                        />
                    </Col>
                </Row>
                )}

                {!singleSearch && (
                    <div className="mt-2 mb-2">
                        <a
                            onClick={e => {

                                e.preventDefault(); setState({ advancedOpen: !advancedOpen });

                            }}
                            className="fw-bold text-decoration-none"
                            href="#"
                        >
                            {<FormattedMessage defaultMessage="Advanced Filters" description={formatMessage({ id: 'new.03d9347f', defaultMessage: 'New' })} id="advanced_filters.6525c61f" />}
                            {' '}
                            {advancedOpen ? '▲' : '▼'}
                        </a>
                    </div>
                )}

                <Collapse in={advancedOpen && !singleSearch}>
                    <div>
                        <Row>
                            <Col className="mb-2" lg={3} md={4}>
                                <GMetrixDropdown
                                    dataItemKey="Id"
                                    dataItems={SECTOR_OPTIONS}
                                    disabled={disabled}
                                    label={formatMessage({ id: 'sector.343b1273', defaultMessage: 'Sector', description: 'New' })}
                                    onChange={(val: typeof SECTOR_OPTIONS[0]) => changeFilters({ sector: val.Id })}
                                    textField="Name"
                                    value={selectedSector}
                                />
                            </Col>
                            <Col className="mb-2" lg={3} md={4}>
                                <GMetrixDropdown
                                    dataItemKey="Id"
                                    dataItems={countries}
                                    disabled={disabled}
                                    label={formatMessage({ id: 'countries.d214f791', defaultMessage: 'Countries', description: 'New' })}
                                    onChange={(val: CountryOption) => changeFilters({ countryId: val.Id })}
                                    textField="Name"
                                    value={selectedCountry}
                                />
                            </Col>
                            <Col className="mb-2" lg={3} md={4}>
                                <GMetrixDropdown
                                    dataItemKey="Id"
                                    dataItems={STATUS_OPTIONS}
                                    disabled={disabled}
                                    label={formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' })}
                                    onChange={(val: typeof STATUS_OPTIONS[0]) => changeFilters({ status: val.Id })}
                                    textField="Name"
                                    value={selectedStatus}
                                />
                            </Col>
                            <Col className="mb-2" lg={3} md={4}>
                                <GMetrixDropdown
                                    dataItemKey="Id"
                                    dataItems={states}
                                    disabled={disabled || filters.countryId === 0}
                                    label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })}
                                    onChange={(val: StateOption) => changeFilters({ stateId: val.Id })}
                                    textField="Name"
                                    value={selectedState}
                                />
                            </Col>
                        </Row>
                    </div>
                </Collapse>
            </Card.Body>

        </Card>
    );

};
